import type { ReactNode } from "react";
import { cssTransition } from "react-toastify";
import Icon from "../Icon/Icon";
import "./Toast.css";

export type ToastType = "success" | "error" | "warning" | "info";

export interface ToastProps {
  type?: ToastType;
  /** main line */
  title?: ReactNode;
  /** optional second line */
  message?: ReactNode;
  /** passed in by react-toastify */
  closeToast?: () => void;
  /** label for the close button, for screen readers */
  closeLabel?: string;
}

/** Enter / exit animation for <ToastContainer transition={toastTransition} /> */
export const toastTransition = cssTransition({
  enter: "ds-toast-enter",
  exit: "ds-toast-exit",
});

const ICON = {
  success: "check",
  error: "close",
  warning: "info",
  info: "info",
} as const;

/**
 * Small message shown at the top centre of the screen.
 * Use it through the `toast` helper in src/helper/common.js:
 *   toast.success("Saved")   toast.error("Something went wrong")
 *   toast.warning("Please select a property")   toast.info("Link copied")
 */
const Toast = ({
  type = "info",
  title,
  message,
  closeToast,
  closeLabel = "Close",
}: ToastProps) => (
  <div
    className={`ds-toast ds-toast--${type}`}
    role={type === "error" ? "alert" : "status"}
  >
    <span className="ds-toast__icon" aria-hidden="true">
      <Icon name={ICON[type]} size={16} />
    </span>
    <div className="ds-toast__text">
      <p className="ds-toast__title">{title}</p>
      {message && <p className="ds-toast__message">{message}</p>}
    </div>
    {closeToast && (
      <button
        type="button"
        className="ds-toast__close"
        aria-label={closeLabel}
        onClick={closeToast}
      >
        <Icon name="close" size={14} />
      </button>
    )}
  </div>
);

export default Toast;
